import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useGetMe, useCompleteSetup, useSendInvite, getGetMeQueryKey } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, CheckCircle2, ArrowRight, Plus, X, Loader2, Building2, Link2, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { COUNTRIES, CURRENCIES } from "@/lib/iso";
import { PhoneInput } from "@/components/PhoneInput";

const INDUSTRIES = [
  "Construction", "Real Estate", "Infrastructure", "Oil & Gas",
  "Utilities", "Manufacturing", "Engineering", "Other"
];

const PRESET_COLORS = [
  "#2563eb", "#7c3aed", "#059669", "#dc2626",
  "#d97706", "#0891b2", "#be185d", "#1a2332"
];

type WizardStep = "profile" | "invite" | "done";
const WIZARD_STEPS: WizardStep[] = ["profile", "invite", "done"];

function StepDot({ step, current }: { step: WizardStep; current: WizardStep }) {
  const idx = WIZARD_STEPS.indexOf(step);
  const curIdx = WIZARD_STEPS.indexOf(current);
  const done = idx < curIdx;
  const active = idx === curIdx;
  return (
    <div className={cn(
      "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all",
      done ? "bg-emerald-500 border-emerald-500 text-white" :
      active ? "bg-blue-600 border-blue-600 text-white" :
      "bg-gray-100 border-gray-200 text-gray-400"
    )}>
      {done ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
    </div>
  );
}

type SlugStatus = "idle" | "checking" | "available" | "taken" | "short";

function toSlug(str: string) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
}

export default function SetupWizard() {
  const [step, setStep] = useState<WizardStep>("profile");
  const { data: me } = useGetMe();
  const [profile, setProfile] = useState({
    industry: "", phone: "", address: "",
    taxLabel: "VAT", taxRate: "15", color: "#2563eb",
    country: "", currency: "",
  });
  const [slug, setSlug] = useState("");
  const [slugStatus, setSlugStatus] = useState<SlugStatus>("idle");
  const [slugDirty, setSlugDirty] = useState(false);
  const slugTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [inviteEmails, setInviteEmails] = useState<string[]>([""]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const completeSetup = useCompleteSetup();
  const sendInvite = useSendInvite();

  // Pre-fill slug from existing company slug on first load
  useEffect(() => {
    if (me?.company?.slug && !slugDirty) {
      setSlug(me.company.slug);
    }
  }, [me?.company?.slug, slugDirty]);

  // Debounced slug availability check
  useEffect(() => {
    if (!slug) { setSlugStatus("idle"); return; }
    if (slug.length < 3) { setSlugStatus("short"); return; }
    // If slug equals the current company slug, it's always available (it's theirs)
    if (slug === me?.company?.slug) { setSlugStatus("available"); return; }
    setSlugStatus("checking");
    if (slugTimer.current) clearTimeout(slugTimer.current);
    slugTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/auth/check-slug?slug=${encodeURIComponent(slug)}`, { credentials: "include" });
        const data = await res.json() as { available: boolean; slug: string };
        setSlug(data.slug); // normalize to server-sanitized form
        setSlugStatus(data.available ? "available" : "taken");
      } catch {
        setSlugStatus("idle");
      }
    }, 500);
    return () => { if (slugTimer.current) clearTimeout(slugTimer.current); };
  }, [slug, me?.company?.slug]);

  const companyId = me?.companyId;

  const setProfileField = (key: string, val: string) =>
    setProfile(prev => ({ ...prev, [key]: val }));

  const handleSaveProfile = async () => {
    if (slugStatus === "taken") {
      toast({ variant: "destructive", title: "URL already taken", description: "Please choose a different company URL before continuing." });
      return;
    }
    setSavingProfile(true);
    const payload = {
      ...profile,
      country: profile.country || me?.company?.country || "",
      currency: profile.currency || me?.company?.currency || "",
      ...(slug && slugStatus !== "taken" && slug.length >= 3 ? { slug } : {}),
    };
    try {
      await completeSetup.mutateAsync({ data: payload });
      await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
      setStep("invite");
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast({ variant: "destructive", title: "Failed to save profile", description: msg || "Please try again." });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSkipProfile = async () => {
    try {
      await completeSetup.mutateAsync({ data: {} });
    } catch { /* ignore */ }
    setStep("invite");
  };

  const handleSendInvites = async () => {
    const validEmails = inviteEmails.filter(e => e.trim() && e.includes("@"));
    if (validEmails.length === 0) {
      setStep("done");
      return;
    }
    setSendingInvites(true);
    let sent = 0;
    for (const email of validEmails) {
      try {
        await sendInvite.mutateAsync({ data: { email: email.trim(), role: "Viewer", companyId: companyId! } });
        sent++;
      } catch { /* ignore individual failures */ }
    }
    setSendingInvites(false);
    if (sent > 0) {
      toast({ title: `${sent} invitation${sent !== 1 ? "s" : ""} sent` });
    }
    setStep("done");
  };

  const handleEnterApp = () => {
    const slug = me?.company?.slug;
    if (slug) {
      setLocation(`/app/${slug}`);
    } else {
      setLocation("/cp");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-10">
          <img
            src="/images/finproject-logo.png"
            alt="FinProject"
            className="w-16 h-16 object-contain mx-auto mb-4 rounded-2xl shadow-lg"
            style={{ border: "2px solid #e5e7eb", padding: "3px", background: "#ffffff" }}
          />
          <h1 className="text-2xl font-bold text-gray-900">Let's set up your workspace</h1>
          <p className="text-gray-500 text-sm mt-2">Takes about 2 minutes — you can skip any step</p>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-center gap-3 mb-10">
          {WIZARD_STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-3">
              <div className="flex flex-col items-center gap-1">
                <StepDot step={s} current={step} />
                <span className={cn("text-xs font-medium", s === step ? "text-gray-900" : "text-gray-400")}>
                  {s === "profile" ? "Company" : s === "invite" ? "Team" : "Done"}
                </span>
              </div>
              {i < WIZARD_STEPS.length - 1 && (
                <div className={cn("h-px w-10 mb-4", WIZARD_STEPS.indexOf(s) < WIZARD_STEPS.indexOf(step) ? "bg-emerald-400" : "bg-gray-200")} />
              )}
            </div>
          ))}
        </div>

        {/* Step: Profile */}
        {step === "profile" && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7 space-y-5">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" /> Company Profile
              </h2>
              <p className="text-sm text-gray-500 mt-1">Tell us more about your company</p>
            </div>

            {/* Company URL slug */}
            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5 text-blue-500" /> Company URL
              </Label>
              <div className="relative">
                <div className="flex items-center rounded-xl border border-gray-300 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
                  <span className="pl-3 pr-1 text-sm text-gray-400 whitespace-nowrap select-none">finproject.app/app/</span>
                  <input
                    type="text"
                    value={slug}
                    onChange={e => {
                      setSlugDirty(true);
                      setSlug(toSlug(e.target.value));
                    }}
                    placeholder={me?.company?.slug || "your-company"}
                    className="flex-1 py-2.5 pr-10 text-sm text-gray-900 bg-transparent outline-none min-w-0 font-mono"
                    maxLength={60}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {slugStatus === "checking" && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                    {slugStatus === "available" && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                    {slugStatus === "taken" && <XCircle className="w-4 h-4 text-rose-500" />}
                    {slugStatus === "short" && <AlertCircle className="w-4 h-4 text-amber-400" />}
                  </div>
                </div>
              </div>
              <p className={cn("text-xs", {
                "text-emerald-600": slugStatus === "available",
                "text-rose-500": slugStatus === "taken",
                "text-amber-500": slugStatus === "short",
                "text-gray-400": slugStatus === "idle" || slugStatus === "checking",
              })}>
                {slugStatus === "available" && "This URL is available"}
                {slugStatus === "taken" && "This URL is already taken — try another"}
                {slugStatus === "short" && "Must be at least 3 characters"}
                {slugStatus === "idle" && "Choose a unique URL for your workspace"}
                {slugStatus === "checking" && "Checking availability…"}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm font-medium">Country</Label>
                <Select value={profile.country || me?.company?.country || ""} onValueChange={v => setProfileField("country", v)}>
                  <SelectTrigger className="h-11 rounded-xl bg-white border-gray-300 text-gray-900">
                    <SelectValue placeholder="Select country..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm font-medium">Currency</Label>
                <Select value={profile.currency || me?.company?.currency || ""} onValueChange={v => setProfileField("currency", v)}>
                  <SelectTrigger className="h-11 rounded-xl bg-white border-gray-300 text-gray-900">
                    <SelectValue placeholder="Select currency..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium">Industry</Label>
              <Select value={profile.industry} onValueChange={v => setProfileField("industry", v)}>
                <SelectTrigger className="h-11 rounded-xl bg-white border-gray-300 text-gray-900">
                  <SelectValue placeholder="Select industry..." />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium">Phone number</Label>
              <PhoneInput
                name="phone"
                defaultValue={profile.phone}
                country={profile.country || me?.company?.country}
                onChange={val => setProfileField("phone", val)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium">Business address</Label>
              <Input value={profile.address} onChange={e => setProfileField("address", e.target.value)}
                placeholder="123 Business Park, Suite 100"
                className="h-11 rounded-xl bg-white border-gray-300 text-gray-900 placeholder:text-gray-400" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm font-medium">Tax label</Label>
                <Input value={profile.taxLabel} onChange={e => setProfileField("taxLabel", e.target.value)}
                  placeholder="VAT"
                  className="h-11 rounded-xl bg-white border-gray-300 text-gray-900 placeholder:text-gray-400" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm font-medium">Tax rate (%)</Label>
                <Input type="number" min="0" max="100" step="0.1" value={profile.taxRate}
                  onChange={e => setProfileField("taxRate", e.target.value)}
                  className="h-11 rounded-xl bg-white border-gray-300 text-gray-900" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium">Brand color</Label>
              <div className="flex items-center gap-3 flex-wrap">
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setProfileField("color", c)}
                    className={cn("w-8 h-8 rounded-lg transition-all border-2",
                      profile.color === c ? "border-gray-900 scale-110 shadow-md" : "border-transparent hover:scale-105"
                    )}
                    style={{ backgroundColor: c }}
                  />
                ))}
                <input type="color" value={profile.color}
                  onChange={e => setProfileField("color", e.target.value)}
                  className="w-8 h-8 rounded-lg cursor-pointer border-0 bg-transparent"
                  title="Custom color" />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={handleSkipProfile}
                className="flex-1 h-11 rounded-xl border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-50">
                Skip for now
              </Button>
              <Button onClick={handleSaveProfile} disabled={savingProfile}
                className="flex-1 h-11 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold border-0 shadow-md">
                {savingProfile ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <>Save & continue <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Invite */}
        {step === "invite" && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7 space-y-5">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" /> Invite Your Team
              </h2>
              <p className="text-sm text-gray-500 mt-1">Add team members — they'll get an email to join</p>
            </div>

            <div className="space-y-3">
              {inviteEmails.map((email, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={e => {
                      const updated = [...inviteEmails];
                      updated[idx] = e.target.value;
                      setInviteEmails(updated);
                    }}
                    placeholder={`teammate${idx + 1}@company.com`}
                    className="h-11 rounded-xl bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 flex-1"
                  />
                  {inviteEmails.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setInviteEmails(inviteEmails.filter((_, i) => i !== idx))}
                      className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-rose-50 text-gray-400 hover:text-rose-500 flex items-center justify-center transition-colors border border-gray-200"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setInviteEmails([...inviteEmails, ""])}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                <Plus className="w-4 h-4" /> Add another
              </button>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep("done")}
                className="flex-1 h-11 rounded-xl border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-50">
                Skip for now
              </Button>
              <Button onClick={handleSendInvites} disabled={sendingInvites}
                className="flex-1 h-11 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold border-0 shadow-md">
                {sendingInvites ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <>Send invites <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Done */}
        {step === "done" && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7 text-center">
            <div className="w-16 h-16 bg-emerald-50 border border-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">You're all set!</h2>
            <p className="text-gray-500 text-sm mb-6">
              Your workspace is ready. Start managing your construction projects right away.
            </p>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-6 text-left space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> Account activated successfully
              </div>
              {me?.company?.name && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> Workspace: {me.company.name}
                </div>
              )}
              {me?.company?.plan && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> Plan: {me.company.plan}
                </div>
              )}
            </div>
            <Button
              onClick={handleEnterApp}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-base border-0 shadow-md"
            >
              Enter App <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
